import type { DayEntry, DayMark, DaySample, Hex, ISODate, Member, MemberId, PostDoc, ProfileDoc } from '../contract.js';
import { firstOfMonth, monthKey, monthOf } from '../dates.js';
import { color } from '../tokens.js';
import type { AppState, Load } from './types.js';

/** Read-only lookups over the app state: the real-data counterparts of the prototype's fixture readers. */

const UNKNOWN: Member = { id: '', name: '', color: color.textMuted, pic: '', isYou: false };

/** The signed-in member. */
export const meOf = (st: AppState): Member => st.session?.me ?? UNKNOWN;
export const isYou = (st: AppState, id: MemberId | null): boolean => !!id && id === st.session?.me.id;
/**
 * `memberOf`: a member by id: you, someone met in the data (as last seen), or a session member.
 * An id nobody has seen gets a neutral placeholder.
 */
export function memberOf(st: AppState, id: MemberId | null): Member {
  if (!st.session || !id) return UNKNOWN;
  if (id === st.session.me.id) return st.session.me;
  return st.people[id] ?? st.session.members.find((member) => member.id === id) ?? { ...UNKNOWN, id };
}

export const todayOf = (st: AppState): ISODate => st.session?.today ?? '';
export const todayMonthKey = (st: AppState): string => (st.session ? monthKey(monthOf(st.session.today)) : '');
export const earliestMonthKey = (st: AppState): string => (st.session ? monthKey(st.session.earliestMonth) : '');
/** The first day that can be opened: the 1st of the earliest month. */
export const earliestDay = (st: AppState): ISODate => (st.session ? firstOfMonth(st.session.earliestMonth) : '');

export const monthStatus = (st: AppState, key: string): Load => st.months[key]?.status ?? 'loading';
/** A day's mark from the month summary, or null when nobody posted (or the month is not loaded). */
export function markOf(st: AppState, date: ISODate): DayMark | null {
  return st.months[monthKey(monthOf(date))]?.days?.[date] ?? null;
}
/** How many people posted on a day. */
export const postersCount = (st: AppState, date: ISODate): number => markOf(st, date)?.count ?? 0;
/** A stripe colour: yours follows your profile at once; everyone else's is as the month was read. */
export const sampleColour = (st: AppState, sample: DaySample): Hex => (sample.id === st.session?.me.id ? st.session.me.color : sample.color);
/** The stripe colours of a day, in member order. */
export const stripeOf = (st: AppState, date: ISODate): readonly Hex[] => (markOf(st, date)?.samples ?? []).map((sample) => sampleColour(st, sample));

export const dayStatus = (st: AppState, date: ISODate): Load => st.days[date]?.status ?? 'loading';
/** The posts of a day once loaded; at most 42 are shown. */
export function entriesOf(st: AppState, date: ISODate): readonly DayEntry[] {
  const day = st.days[date];
  return day?.status === 'loaded' && day.entries ? day.entries.slice(0, 42) : [];
}

/** `mineFor`: your post on a day, when that day is loaded. */
export function mineFor(st: AppState, date: ISODate): PostDoc | null {
  const mine = st.session?.me.id;
  return st.days[date]?.entries?.find((entry) => entry.memberId === mine)?.post ?? null;
}
/** Whether you already posted today (drives Post today / Edit). */
export function postedToday(st: AppState): boolean {
  if (!st.session) return false;
  const today = st.session.today, mine = st.session.me.id;
  const day = st.days[today];
  if (day?.status === 'loaded' && day.entries) return day.entries.some((entry) => entry.memberId === mine);
  return markOf(st, today)?.mine ?? false;
}

export const profileOfMember = (st: AppState, id: MemberId | null): ProfileDoc | null => (id ? st.profiles[id]?.doc ?? null : null);
